import React, { useState, useEffect, useRef } from 'react'
import CatBigImg from '@/assets/img/cat_big.png'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import RecordingBox from '@/components/atoms/RecordingBox/RecordingBox'
import clsx from 'clsx'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface QuestionAnswer {
  textAnswer?: string;
  recorded?: boolean;
  blob?: Blob;
  url?: string;
  isCorrect?: boolean;
  listened?: boolean;
  wrongCount?: number;
  revealAnswer?: boolean;
}

interface Question {
  id: string;
  text: string;
  imageUrl: string;
  audioUrl: string | null;
  correctAnswer?: string; // 정답
}

interface StoryDialogQAProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: { 
    questionAnswers?: Record<string, QuestionAnswer>;
    isCorrect?: boolean;
    isHomeworkReady?: boolean;
  }) => void;
  existingResponse?: { 
    questionAnswers?: Record<string, QuestionAnswer>;
    isCorrect?: boolean;
  };
  isHomework?: boolean;
}

export default function StoryDialogQA({ activity, unitData, onResponse, existingResponse, isHomework = false }: StoryDialogQAProps) {
  // configJson에서 recordingEnabled 확인
  let config: any = {};
  try {
    if (activity?.configJson) {
      config = typeof activity.configJson === 'string' 
        ? JSON.parse(activity.configJson) 
        : activity.configJson;
    }
  } catch (e) {
    console.error('configJson 파싱 에러:', e);
  }
  
  const recordingEnabled = config.recordingEnabled === true;
  const title = getStudentActivityTitle(activity?.title, '스토리&다이얼로그_퀘스천');
const WRONG_LIMIT = 5;
  
  console.log('=== StoryDialogQA ===');
  console.log('Activity Items:', activity?.items);
  
  // activity.items에서 모든 질문 데이터 가져오기
  const items = activity?.items || [];
  const questions: Question[] = items.map((item: any, index: number) => {
    let content: any = {};
    try {
      content = typeof item.contentJson === 'string' 
        ? JSON.parse(item.contentJson) 
        : item.contentJson;
    } catch (e) {
      console.error('contentJson 파싱 에러:', e);
    }
    
    return {
      id: `question-${index}`,
      text: content.sentence || content.text || content.question || content.content || '',
      imageUrl: content.imageUrl || content.image,
      audioUrl: content.audioUrl || content.audio || null,
      correctAnswer: content.answer || '' // DB의 answer 필드
    };
  });
  
  console.log('Questions:', questions);
  
  const getInitialQuestionAnswers = () => {
    const base = existingResponse?.questionAnswers || {};
    const normalized: Record<string, QuestionAnswer> = {};
    questions.forEach((question) => {
      const prev = base[question.id] || {};
      const wrongCount = prev?.wrongCount ?? 0;
      normalized[question.id] = {
        ...prev,
        wrongCount,
        revealAnswer: prev?.revealAnswer ?? (wrongCount >= WRONG_LIMIT)
      };
    });
    return normalized;
  };
  const getInitialListenState = () => {
    const initial: Record<string, boolean> = {};
    const answers = existingResponse?.questionAnswers || {};
    questions.forEach((q) => {
      if (!q.audioUrl) {
        initial[q.id] = true;
      } else {
        const prev = answers[q.id]?.listened;
        initial[q.id] = Boolean(prev);
      }
    });
    return initial;
  };

  const [questionAnswers, setQuestionAnswers] = useState<Record<string, QuestionAnswer>>(getInitialQuestionAnswers);
  const [listenedState, setListenedState] = useState<Record<string, boolean>>(getInitialListenState);
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});

  useEffect(() => {
    setQuestionAnswers(getInitialQuestionAnswers());
    setListenedState(getInitialListenState());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity?.items, existingResponse]);

  const getByteLength = (value: string) => {
    let len = 0;
    for (let i = 0; i < value.length; i += 1) {
      len += value.charCodeAt(i) > 127 ? 2 : 1;
    }
    return len;
  };

  const computeHomeworkReady = (
    state: Record<string, QuestionAnswer>,
    listens: Record<string, boolean>
  ) => {
    return questions.every((question) => {
      const current = state[question.id];
      const textLength = getByteLength((current?.textAnswer || '').trim());
      const listened = !question.audioUrl || listens[question.id] === true;
      const recorded = !recordingEnabled || current?.recorded === true;
      return textLength >= 1 && listened && recorded;
    });
  };

  const handleAudioEnded = (questionId: string) => {
    setListenedState(prev => ({ ...prev, [questionId]: true }));
    setQuestionAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        listened: true
      }
    }));
  };

  const playQuestionAudio = (questionId: string) => {
    const audio = audioRefs.current[questionId];
    if (!audio) {
      return;
    }
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.play().catch((error) => {
        console.error('StoryDialogQA 오디오 재생 실패:', error);
      });
    } catch (error) {
      console.error('StoryDialogQA 오디오 제어 실패:', error);
    }
  };

  const handleWrongAnswer = (question: Question) => {
    const questionId = question.id;
    setQuestionAnswers((prev) => {
      const prevAnswer = prev[questionId] || {};
      const currentCount = prevAnswer.wrongCount ?? 0;
      const nextWrongCount = Math.min(WRONG_LIMIT, currentCount + 1);
      const shouldReveal = prevAnswer.revealAnswer === true || nextWrongCount >= WRONG_LIMIT;
      const nextAnswer: QuestionAnswer = {
        ...prevAnswer,
        isCorrect: false,
        wrongCount: nextWrongCount,
        revealAnswer: shouldReveal,
        ...(recordingEnabled ? { recorded: false, url: undefined, blob: undefined } : {})
      };
      const updated = { ...prev, [questionId]: nextAnswer };
      if (onResponse) {
        onResponse({
          questionAnswers: updated,
          isCorrect: checkCompletionCondition(updated),
          isHomeworkReady: computeHomeworkReady(updated, listenedState)
        });
      }
      return updated;
    });

    if (question.audioUrl) {
      playQuestionAudio(questionId);
    }
  };
  
  // 개별 질문 텍스트 답변 변경
  const handleTextChange = (questionId: string, value: string) => {
    const newState = {
      ...questionAnswers,
      [questionId]: {
        ...questionAnswers[questionId],
        textAnswer: value,
        isCorrect: undefined,
        ...(recordingEnabled ? { recorded: false, url: undefined, blob: undefined } : {})
      }
    };
    
    setQuestionAnswers(newState);
    
    if (onResponse) {
      const homeworkReady = computeHomeworkReady(newState, listenedState);
      if (isHomework) {
        // 숙제 모드: 항상 응답 저장 (입력할 때마다)
        console.log('숙제 모드 - 응답 저장:', questionId, value);
        onResponse({ 
          questionAnswers: newState,
          isCorrect: true,
          isHomeworkReady: homeworkReady
        });
      } else if (onResponse) {
        onResponse({ 
          questionAnswers: newState,
          isCorrect: false,
          isHomeworkReady: homeworkReady
        });
      }
    }
  };

  const handleConfirmAnswer = (question: Question) => {
    const questionId = question.id;
    const typed = (questionAnswers[questionId]?.textAnswer || '').trim();
    const correctAnswer = (question.correctAnswer || '').trim();
    const isCorrectAnswer = typed === correctAnswer;

    if (!isCorrectAnswer) {
      handleWrongAnswer(question);
      return;
    }

    const updated = {
      ...questionAnswers,
      [questionId]: {
        ...questionAnswers[questionId],
        isCorrect: true,
      }
    };

    setQuestionAnswers(updated);
    if (onResponse) {
      const isComplete = checkCompletionCondition(updated);
      onResponse({
        questionAnswers: updated,
        isCorrect: isComplete,
        isHomeworkReady: computeHomeworkReady(updated, listenedState)
      });
    }
  };
  
  // 완료 조건 확인 함수
  const checkCompletionCondition = (state: Record<string, QuestionAnswer>) => {
    if (isHomework) {
      // 숙제 모드: 항상 완료
      return true;
    }
    
    // 학습 모드
    if (recordingEnabled) {
      // 녹음 활성화: 모든 질문의 녹음 + 정답 체크
      return questions.every(q => {
        const answer = state[q.id];
        return answer?.isCorrect === true && answer?.recorded === true;
      });
    }
    // 녹음 비활성화: 모든 질문의 정답만 체크
    return questions.every(q => state[q.id]?.isCorrect === true);
  };
  
  // 개별 질문 녹음 완료
  const handleRecordingComplete = (questionId: string, audioBlob: Blob, audioUrl: string) => {
    console.log(`질문 ${questionId} 녹음 완료:`, audioBlob);
    
    if (!recordingEnabled) {
      return;
    }

    if (!isHomework && questionAnswers[questionId]?.isCorrect !== true) {
      console.warn('정답 입력 후 녹음이 가능합니다.');
      return;
    }
    
    const newState = {
      ...questionAnswers,
      [questionId]: {
        ...questionAnswers[questionId],
        recorded: true,
        blob: audioBlob,
        url: audioUrl
      }
    };
    
    setQuestionAnswers(newState);
    
    if (onResponse) {
      const homeworkReady = computeHomeworkReady(newState, listenedState);
      if (isHomework) {
        // 숙제 모드: 항상 응답 저장
        console.log('숙제 모드 - 녹음 응답 저장:', questionId);
        onResponse({ 
          questionAnswers: newState,
          isCorrect: true,
          isHomeworkReady: homeworkReady
        });
      } else {
        // 학습 모드: 완료 조건 확인
        const isComplete = checkCompletionCondition(newState);
        
        if (isComplete) {
          console.log('✅ 완료 조건 충족 - 다음 버튼 활성화');
          onResponse({ 
            questionAnswers: newState,
            isCorrect: true,
            isHomeworkReady: homeworkReady
          });
        } else {
          console.log('진행 중 - 완료 조건 미충족');
        }
      }
    }
  };
  
  // 컴포넌트 마운트 시 초기 응답 저장
  useEffect(() => {
    if (existingResponse && existingResponse.questionAnswers && Object.keys(existingResponse.questionAnswers).length > 0) {
      console.log('StoryDialogQA: 기존 답변 데이터 복원');
    } else if (onResponse) {
      // 초기 응답 저장
      console.log('StoryDialogQA: 초기 응답 저장');
      onResponse({
        questionAnswers: getInitialQuestionAnswers(),
        isCorrect: isHomework ? true : false, // 숙제는 항상 완료, 학습은 녹음 필요
        isHomeworkReady: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  
  return (
    <main>
      <div>
        <h1 className="px-5 pt-5 pb-3 heading1">{title}</h1>
        <div className="flex flex-col px-5 pt-0 space-y-5">
          {questions.map((question: Question, index: number) => {
            const currentAnswer = questionAnswers[question.id] || {};
            const wrongCount = currentAnswer.wrongCount ?? 0;
            const shouldRevealAnswer = currentAnswer.revealAnswer === true;
            const textValue = currentAnswer.textAnswer || '';

            return (
              <section 
                key={question.id}
                className="flex flex-col overflow-hidden border border-gray-200 rounded-2xl lg:flex-row lg:items-start"
              >
                <div className="relative flex flex-col items-center justify-center w-full gap-2 lg:max-w-[300px]">
                  {question.imageUrl ? (
                    <img
                      src={question.imageUrl}
                      alt=""
                      className="object-cover border border-gray-100 rounded-2xl aspect-video"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = CatBigImg;
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full bg-gray-100 border border-gray-100 rounded-2xl aspect-video text-gray-500 font-bold">
                      이미지가 없습니다.
                    </div>
                  )}
                  <ListenButton 
                    size={48} 
                    className="absolute right-2 top-2" 
                    audioSrc={question.audioUrl}
                    onAudioEnded={() => handleAudioEnded(question.id)}
                  />
                  {question.audioUrl && (
                    <audio
                      ref={(el) => { audioRefs.current[question.id] = el }}
                      src={question.audioUrl}
                      onEnded={() => handleAudioEnded(question.id)}
                      style={{ display: 'none' }}
                    />
                  )}
                </div>

                <div className="w-full p-5">
                  <div className="flex items-center w-full gap-2">
                    <p className="flex items-center justify-center text-base font-bold text-white bg-indigo-500 rounded-full size-7">
                      Q{index + 1}
                    </p>
                    <span className="font-bold">{question.text}</span>
                  </div>
                  <div>
                    <textarea
                      placeholder="문장을 입력 해주세요."
                      value={textValue}
                      onChange={(e) => handleTextChange(question.id, e.target.value)}
                      className={clsx(
                        styles.graInput,
                        'mt-4 resize-none',
                        !listenedState[question.id] && 'opacity-50 cursor-not-allowed'
                      )}
                      disabled={!listenedState[question.id]}
                    />
                    {!listenedState[question.id] && (
                      <p className="mt-2 text-xs text-red-500">
                        음원을 끝까지 들으면 입력할 수 있습니다.
                      </p>
                    )}
                    {!isHomework && (
                      <div className="flex justify-end mt-3">
                        <button
                          type="button"
                          className={clsx(
                            'px-4 py-2 text-sm font-semibold rounded-lg border',
                            getByteLength(textValue.trim()) >= 1 && listenedState[question.id]
                              ? 'text-white bg-indigo-500 border-indigo-500'
                              : 'text-gray-400 border-gray-200 bg-gray-50 cursor-not-allowed'
                          )}
                          disabled={
                            !listenedState[question.id] ||
                            getByteLength(textValue.trim()) < 1
                          }
                          onClick={() => handleConfirmAnswer(question)}
                        >
                          확인
                        </button>
                      </div>
                    )}
                    {!isHomework && wrongCount > 0 && wrongCount < WRONG_LIMIT && (
                      <p className="mt-2 text-sm text-gray-500">
                        <span className="text-red-500">오답 {wrongCount}회</span> - 다시 듣고 입력해주세요.
                      </p>
                    )}
                    {!isHomework && shouldRevealAnswer && (
                      <p className="mt-2 text-sm font-semibold text-orange-600">
                        정답: {question.correctAnswer || '정답 정보가 없습니다.'}
                      </p>
                    )}
                  </div>
                  
                  {/* 녹음 UI (recordingEnabled가 true일 때만 표시) */}
                  {recordingEnabled && (
                    <div className="mt-3">
                      <div
                        className={clsx(
                          !isHomework && !currentAnswer?.isCorrect && 'opacity-40 pointer-events-none'
                        )}
                      >
                        <RecordingBox 
                          className="mb-0"
                          onRecordingComplete={(blob, url) => handleRecordingComplete(question.id, blob, url)}
                          existingAudioUrl={currentAnswer?.url || null}
                        />
                      </div>
                      {!isHomework && currentAnswer?.isCorrect !== true && (
                        <p className="mt-1 text-xs text-gray-500">
                          정답을 맞춘 후 녹음할 수 있습니다.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </main>
  );
}